import type React from "react";

// ── API response ────────────────────────────────────────────────────────────

export type ClaimLandingApiResponse = Record<string, unknown> | null;

// ── API component → internal card-name mapping ──────────────────────────────

export const COMPONENT_ID_TO_CARD_NAME: Record<string, string> = {
  COMPONENT_CLAIMS_RAISE_NEW_CLAIM_CARD: "RaiseNewClaimCard",
  COMPONENT_CLAIMS_FAQ_CARD: "FAQCard",
  COMPONENT_CLAIMS_STATUS_CARD: "ClaimStatusCard",
};

// ── API component entry (raw from backend) ──────────────────────────────────

export interface ApiComponentEntry {
  id?: string;
  data?: unknown;
}

// ── Card block (normalized) ─────────────────────────────────────────────────

export type ClaimCardBlock = {
  cardName?: string;
  data?: unknown;
};

// ── Page-level metadata ─────────────────────────────────────────────────────

export interface ClaimPageData {
  bookingId?: string;
  currency?: string;
}

// ── Normalized data ─────────────────────────────────────────────────────────

export type ClaimLandingNormalized = {
  headingTitle: string;
  pageData: ClaimPageData;
  cardList: ClaimCardBlock[];
  byCardName: Record<string, unknown>;
  raw: unknown;
};

// ── Wrapper & layout props ──────────────────────────────────────────────────

export type ClaimsWrapperProps = {
  data: ClaimLandingApiResponse;
};

export type ClaimsDesktopProps = {
  normalizedData: ClaimLandingNormalized;
};

export type ClaimsPWAProps = {
  normalizedData: ClaimLandingNormalized;
};

// ── View state ───────────────────────────────────────────────────────────────

export type ClaimContentType = "staticPage" | "claimFlow";

export type ClaimViewState = "landing" | "info";

export interface ClaimInfoViewParams {
  detailFetchParams: ClaimDetailFetchParams;
  categoryTitle?: string;
  categorySubtitle?: string;
  categoryIcon?: string;
  contentType: ClaimContentType;
}

// ── Context ─────────────────────────────────────────────────────────────────

export type ClaimsContextValue = ClaimLandingNormalized & {
  refreshClaimData: () => void;
  activeView: ClaimViewState;
  infoViewParams: ClaimInfoViewParams | null;
  navigateToInfo: (params: ClaimInfoViewParams) => void;
  navigateToLanding: () => void;
};

// ── Renderer types ──────────────────────────────────────────────────────────

export interface ClaimsCardItem {
  cardName: string;
  data: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ClaimsComponentMapperType = Record<
  string,
  Record<string, React.ComponentType<any>>
>;

export interface ClaimsComponentRendererProps {
  cards?: ClaimsCardItem[];
  layout: "left" | "right" | "top" | "rest";
  mapper?: ClaimsComponentMapperType;
}

// ── FormattedText-compatible text node ──────────────────────────────────────

export interface TextNode {
  text?: {
    value?: string;
    styles?: Record<string, string>;
  };
}

// ── ClaimStatusCard types ──────────────────────────────────────────────────

export interface ClaimedCardStyle {
  backgroundColor?: string;
  cornerRadius?: string;
  titleColor?: string;
  claimTypeTitleColor?: string;
  bottomBorderColor?: string | string[];
}

export interface ClaimedCardTraveller {
  name?: string;
  gender?: string;
  age?: string;
}

export interface ViewDetailData {
  title?: TextNode;
  travellers?: ClaimedCardTraveller[];
}

export interface AddBankDetailField {
  key?: string;
  placeholder?: string;
  type?: string;
}

export interface AddBankDetailData {
  title?: TextNode;
  fields?: AddBankDetailField[];
  submitButton?: {
    text?: { value?: string; styles?: Record<string, string> };
    style?: {
      background?: string;
      color?: string;
      cornerRadius?: string;
    };
  };
}

export interface ClaimedCardAction {
  actionId?: string;
  title?: TextNode;
  titleColor?: string;
  icon?: { url?: string; size?: { width?: number; height?: number } };
  viewDetailData?: ViewDetailData;
  addBankDetailData?: AddBankDetailData;
}

export interface ClaimedCard {
  state?: string;
  title?: TextNode;
  claimTypeTitle?: TextNode;
  icon?: string;
  style?: ClaimedCardStyle;
  action?: ClaimedCardAction;
}

export interface ClaimStatusCardProps {
  title?: TextNode;
  noClaimsTitle?: TextNode;
  claimedCards?: ClaimedCard[];
}

// ── RaiseNewClaimCard types ─────────────────────────────────────────────────

export interface ClaimDetailFetchParams {
  categoryId?: string;
  nodeId?: string;
}

export interface ClaimReasonNode {
  id?: string;
  title?: TextNode;
  subtitle?: TextNode;
  detailFetchParams?: ClaimDetailFetchParams;
  contentType?: ClaimContentType;
}

export interface ClaimReasonData {
  title?: TextNode;
  nodes?: ClaimReasonNode[];
}

export interface ClaimCategory {
  id?: string;
  type?: "static" | "separateNode" | string;
  title?: TextNode;
  icon?: string;
  showChevron?: boolean;
  contentType?: ClaimContentType;
  detailFetchParams?: ClaimDetailFetchParams;
  actionId?: string;
  reasonData?: ClaimReasonData;
}

export interface RaiseNewClaimPolicyCard {
  provider?: TextNode;
  route?: TextNode;
  travelDates?: TextNode;
  pax?: TextNode;
  viewDetailData?: ViewDetailData;
  style?: {
    backgroundColor?: string;
    cornerRadius?: string;
  };
}

export interface RaiseNewClaimCardProps {
  title?: TextNode;
  policyCard?: RaiseNewClaimPolicyCard;
  claimPrompt?: TextNode;
  claimCategories?: ClaimCategory[];
  disclaimerBox?: {
    message?: TextNode;
    style?: { backgroundColor?: string };
  };
}

// ── Static page (benefit) types ─────────────────────────────────────────────

export interface StaticPageSectionItem {
  name?: TextNode;
  description?: TextNode;
}

export interface StaticPageSection {
  id?: number;
  title?: TextNode;
  items?: StaticPageSectionItem[];
}

export interface ClaimCategoryDetailsData {
  iconUrl?: string;
  title?: TextNode;
  subtitle?: TextNode;
  sections?: StaticPageSection[];
}

export interface HelpContactCardData {
  title?: TextNode;
  description?: TextNode;
  contactInfo?: { text?: string };
  backgroundColor?: string;
}

export interface StaticPageBodyComponent {
  cardName?: string;
  data?: Record<string, unknown>;
}

export interface ClaimStaticPageData {
  headerTitle?: string;
  bodyComponents?: StaticPageBodyComponent[];
}

// ── ClaimInfoView props ─────────────────────────────────────────────────────

export interface ClaimInfoViewProps {
  params: ClaimInfoViewParams;
  bookingId: string;
  currency: string;
  onBack: () => void;
}
